/* eslint-disable max-lines */
// Library methods

// Utils
import { GET, POST } from '../../../utils/fetchWrappers';

// API URLs
import {
    API_CREATE_SESSION,
    API_LOGIN,
    API_LOGOUT,
    API_FORGOT_PASSWORD,
    API_RESET_PASSWORD,
    API_IS_LOGGED_IN,
    API_GET_USER_PROFILE,
    API_MARK_USER_ACTIVE,
    API_GET_ACCESSIBLE_ITEMS_FOR_USER,
    API_OTP_VALIDATE,
    API_OTP_RESEND,
} from '../../../utils/API/apiUrls';

// API Status Codes
import { apiStatusCodes } from '../../../utils/API/apiStatusCodes';

// Helper Methods
import {
    setToLocalStorage,
    getFromLocalStorage,
} from '../../../utils/helperMethods';

// Toast Messages
import {
    ERROR_MESSAGES,
    SUCCESS_MESSAGES,
} from '../../../utils/constants/toastMessages';

// Browser Storage Field Names
import { LOCAL_STORAGE_FIELD_NAMES } from '../../../utils/constants/browserStorageFieldNames';

// Redux Actions
import * as loaderActions from '../loader/loader.actions';
import * as authActions from './auth.actions';

// Action Constants
import {
    CREATE_SESSION_REQUEST,
    LOGIN_REQUEST,
    IS_LOGGED_IN_REQUEST,
    USER_PROFILE_REQUEST,
    LOGOUT_REQUEST,
    FORGOT_PASSWORD_REQUEST,
    RESET_PASSWORD_REQUEST,
    MARK_USER_ACTIVE_REQUEST,
    GET_USER_ACCESSIBLE_ITEMS_REQUEST,
    OTP_RESEND_REQUEST,
    OTP_VALIDATE_REQUEST,
} from './auth.constants';
import { toast } from 'react-toastify';
import { takeLatest, put, call } from 'redux-saga/effects';
import { MOCK_API_VALUE } from 'utils/constants/commonValues';
import sessionRes from 'mockApiWiseReqRes/mockapis/session.res';
import isLoggedIn from 'mockApiWiseReqRes/mockapis/isloggedin.res';

// Create user session
export function* createSession(actions) {
    const { history } = actions;
    try {
        let data;
        if (MOCK_API_VALUE) {
            data = sessionRes();
        } else {
            const response = yield call(GET, API_CREATE_SESSION);
            data = response?.data;
        }

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            const storedcsrfIdentifier = getFromLocalStorage(
                LOCAL_STORAGE_FIELD_NAMES.CSRF_IDENTIFIER
            );

            yield put(
                authActions.createSessionSuccess({
                    csrfIdentifier: storedcsrfIdentifier,
                })
            );
        }
    } catch ({ err }) {
        const errorMessage = ERROR_MESSAGES.CREATE_SESSION;
        history.replace('/error');
        toast.error(errorMessage);
    }
}

// Login User
export function* loginUser({ payload }) {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(POST, API_LOGIN, { ...payload });
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.loginSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                authActions.loginError({ loginError: data.errorMessage })
            );
        }
    } catch ({ response }) {
        const { data } = response;

        const errorMessage =
            (data && data.errorMessage) || ERROR_MESSAGES.LOGIN;
        yield put(authActions.loginError({ loginError: errorMessage }));
    }

    yield put(loaderActions.hideLoader());
}

// Check is logged in
export function* checkIsLoggedIn() {
    yield put(loaderActions.showLoader());

    try {
        let data;
        if (MOCK_API_VALUE) {
            data = isLoggedIn();
        } else {
            const response = yield call(GET, API_IS_LOGGED_IN);
            data = response.data;
        }

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(
                authActions.isLoggedInSuccess({ isLoggedIn: data.loggedIn })
            );
            setToLocalStorage(
                LOCAL_STORAGE_FIELD_NAMES.IS_LOGGED_IN,
                data.loggedIn
            );
        }
    } catch ({ err }) {
        console.error('error', err);
    }

    yield put(loaderActions.hideLoader());
}

// Get User Profile
export function* getUserProfile() {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(GET, API_GET_USER_PROFILE);
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.userProfileSuccess(data.userDetails));
        }
    } catch ({ err }) {
        console.error('error', err);
    }

    yield put(loaderActions.hideLoader());
}

// Logout User
export function* logoutUser() {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(POST, API_LOGOUT, {});
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.logoutSuccess());
            setToLocalStorage(LOCAL_STORAGE_FIELD_NAMES.IS_LOGGED_IN, false);

            toast.info(SUCCESS_MESSAGES.LOGOUT_SUCCESS);
        }
    } catch ({ response }) {
        const { data } = response;

        const errorMessage =
            (data && data.errorMessage) || ERROR_MESSAGES.LOGOUT;
        toast.error(errorMessage);
    }

    yield put(loaderActions.hideLoader());
}

// Forgot Password
export function* forgotPassword({ payload }) {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(POST, API_FORGOT_PASSWORD, { ...payload });
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.forgotPasswordSuccess());

            toast.info(SUCCESS_MESSAGES.FORGOT_PASSWORD);
        } else if (data && data.errorMessage) {
            yield put(
                authActions.forgotPasswordError({
                    forgotPasswordError: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;

        const errorMessage =
            (data && data.errorMessage) || ERROR_MESSAGES.FORGOT_PASSWORD;
        yield put(
            authActions.forgotPasswordError({
                forgotPasswordError: errorMessage,
            })
        );
    }

    yield put(loaderActions.hideLoader());
}

// Reset Password
export function* resetPassword({ payload }) {
    yield put(loaderActions.showLoader());

    try {
        const { pageType, ...reqPayload } = payload;
        const response = yield call(POST, API_RESET_PASSWORD, {
            ...reqPayload,
        });
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.resetPasswordSuccess());

            if (pageType === 'reset') {
                toast.info(SUCCESS_MESSAGES.RESET_PASSWORD);
            } else {
                toast.info(SUCCESS_MESSAGES.SET_PASSWORD);
            }
            window.location.replace('/login');
        } else if (data && data.errorMessage) {
            yield put(
                authActions.resetPasswordError({
                    resetPasswordError: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;

        const errorMessage =
            (data && data.errorMessage) || ERROR_MESSAGES.RESET_PASSWORD;
        yield put(
            authActions.resetPasswordError({
                resetPasswordError: errorMessage,
            })
        );
    }

    yield put(loaderActions.hideLoader());
}

// Mark User Active
export function* markUserActive({ payload }) {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(POST, API_MARK_USER_ACTIVE, { ...payload });
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.markUserActiveSuccess());
        } else if (data && data.errorMessage) {
            toast.info(data.errorMessage);
        }
    } catch ({ response }) {
        const { data } = response;

        const errorMessage =
            (data && data.errorMessage) || ERROR_MESSAGES.MARK_USER_ACTIVE;
        toast.info(errorMessage);
    }

    yield put(loaderActions.hideLoader());
}

// Get User Accessible Items
export function* getAccessibleItems() {
    yield put(loaderActions.showLoader());

    try {
        const request = {};
        const response = yield call(
            POST,
            API_GET_ACCESSIBLE_ITEMS_FOR_USER,
            request
        );
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(
                authActions.getUserAccessibleItemsSuccess({
                    resourceDetails: data.resourceDetail,
                })
            );
        }
    } catch (err) {
        const errorMessage = ERROR_MESSAGES.CREATE_SESSION;
        toast.error(errorMessage);
    }

    yield put(loaderActions.hideLoader());
}

// Resend Otp
export function* getResendOtp({ payload }) {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(POST, API_OTP_RESEND, payload);
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.resendOtpSuccess(data));
        } else if (data && data.errorMessage) {
            toast.info(data.errorMessage);
            yield put(
                authActions.resetPasswordError({ error: data.errorMessage })
            );
        }
    } catch ({ response }) {
        const { data, status } = response;
        const errorMessage = data && data.errorMessage;
        if (status === 401) {
            yield put(authActions.resetOtpFlag());
            yield put(authActions.resetPasswordError({ error: '' }));
            toast.info(errorMessage);
        } else {
            yield put(authActions.resetPasswordError({ error: errorMessage }));
            toast.info(errorMessage);
        }
    }

    yield put(loaderActions.hideLoader());
}

// Validate Api
export function* getValidateOtp({ payload }) {
    yield put(loaderActions.showLoader());

    try {
        const response = yield call(POST, API_OTP_VALIDATE, payload);
        const { data } = response;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(authActions.validateOtpSuccess(data));
            setToLocalStorage(LOCAL_STORAGE_FIELD_NAMES.IS_LOGGED_IN, true);
            toast.info(SUCCESS_MESSAGES.LOGIN_SUCCESS_WELCOME(data.name));
        } else if (data && data.errorMessage) {
            yield put(
                authActions.validateOtpError({ error: data.errorMessage })
            );
        }
    } catch ({ response }) {
        const { data, status } = response;
        const errorMessage = data && data.errorMessage;
        if (status === 401) {
            yield put(authActions.resetOtpFlag());
            yield put(authActions.validateOtpError({ error: '' }));
            toast.info(errorMessage);
        } else if (errorMessage) {
            yield put(authActions.validateOtpError({ error: errorMessage }));
        }
    }

    yield put(loaderActions.hideLoader());
}

/**
 * WATCHER SAGA
 */
export function* authSagas() {
    yield takeLatest(CREATE_SESSION_REQUEST, createSession);
    yield takeLatest(LOGIN_REQUEST, loginUser);
    yield takeLatest(IS_LOGGED_IN_REQUEST, checkIsLoggedIn);
    yield takeLatest(USER_PROFILE_REQUEST, getUserProfile);
    yield takeLatest(LOGOUT_REQUEST, logoutUser);
    yield takeLatest(FORGOT_PASSWORD_REQUEST, forgotPassword);
    yield takeLatest(RESET_PASSWORD_REQUEST, resetPassword);
    yield takeLatest(MARK_USER_ACTIVE_REQUEST, markUserActive);
    yield takeLatest(GET_USER_ACCESSIBLE_ITEMS_REQUEST, getAccessibleItems);
    yield takeLatest(OTP_RESEND_REQUEST, getResendOtp);
    yield takeLatest(OTP_VALIDATE_REQUEST, getValidateOtp);
}
